import { Text } from "@/elements"
import { FragmentType, graphql } from "@/gql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import { JobPostingLocationType, JobTitleColorisedFragmentDoc } from "@/gql/graphql"
import { TextColor, TextVariant } from "@/theme"
import adjustDateForTimezone from "@/utils/dates"
import { formatDate } from "date-fns"
import indefinite from "indefinite"
import React from "react"

graphql(`
  fragment JobTitleColorised on JobPosting {
    id
    __typename
    locationName
    locationType
    position {
      id
      name
    }
    startDate
    endDate
    datesAreFlexible
    creativeWorkType {
      id
      __typename
      name
    }
  }
`)

type JobTitleBuilderPart = {
  text: string
  className?: string
  key: string
}

interface GetTitlePartsProps {
  jobPost: FragmentType<typeof JobTitleColorisedFragmentDoc> | null | undefined
  commonWordColor?: TextColor
  focusWordColor?: TextColor
}
export const getJobTitleParts = (props: GetTitlePartsProps) => {
  const jobPost = getFragmentData(JobTitleColorisedFragmentDoc, props.jobPost)
  const { commonWordColor, focusWordColor } = props
  let jobTitleParts: JobTitleBuilderPart[] = []

  if (jobPost?.position?.name) {
    jobTitleParts.push({
      text: jobPost.position.name,
      className: focusWordColor,
      key: "position-name",
    })
    jobTitleParts.push({
      text: " needed",
      className: commonWordColor,

      key: "needed",
    })
  }
  if (jobPost?.creativeWorkType?.name) {
    if (jobTitleParts.length > 0) {
      jobTitleParts.push({
        text: " for " + indefinite(jobPost.creativeWorkType.name, { articleOnly: true }) + " ",
        className: commonWordColor,

        key: "creative-work-type-for",
      })
    }
    jobTitleParts.push({
      text: jobPost.creativeWorkType.name,
      className: focusWordColor,
      key: "creative-work-type",
    })
  }

  if (jobPost?.locationType === JobPostingLocationType.Telecommute) {
    jobTitleParts.push({
      text: " to work",
      className: commonWordColor,
      key: "creative-work-type-1",
    })
    jobTitleParts.push({
      text: " remotely",
      className: focusWordColor,
      key: "creative-work-type-2",
    })
  }
  if (jobPost?.locationName && jobPost.locationType !== JobPostingLocationType.Telecommute) {
    jobTitleParts.push({
      text: " in ",
      className: commonWordColor,
      key: "location-type",
    })
    jobTitleParts.push({
      text: jobPost.locationName,
      className: focusWordColor,
      key: "location-name",
    })
  }

  if (jobPost?.startDate || jobPost?.endDate) {
    if (jobPost.startDate && jobPost.endDate) {
      jobTitleParts.push({
        text: " from ",
        className: commonWordColor,
        key: "start-date-from",
      })
      jobTitleParts.push({
        text:
          formatDate(adjustDateForTimezone(jobPost.startDate || "")!, "MM/dd") +
          " - " +
          formatDate(adjustDateForTimezone(jobPost.endDate || "")!, "MM/dd"),
        className: focusWordColor,
        key: "start-date-range",
      })
    }
    if (jobPost.startDate && !jobPost.endDate) {
      jobTitleParts.push({
        text: " on ",
        className: commonWordColor,

        key: "start-date-on",
      })
      jobTitleParts.push({
        text: formatDate(adjustDateForTimezone(jobPost.startDate || "")!, "MM/dd"),
        className: focusWordColor,

        key: "start-date",
      })
    }
  }

  if (jobTitleParts[jobTitleParts.length - 1]?.text === " needed") {
    jobTitleParts.push({
      text: ".",
      className: commonWordColor,
      key: "final-period-common",
    })
  } else if (jobPost?.datesAreFlexible) {
    jobTitleParts.push({
      text: ". ",
      className: focusWordColor,
      key: "previous-period",
    })
    jobTitleParts.push({
      text: " Dates flexible.",
      className: commonWordColor,
      key: "dates-flexible",
    })
  } else {
    jobTitleParts.push({
      text: ".",
      className: focusWordColor,
      key: "final-period",
    })
  }
  return jobTitleParts
}

interface JobTitleColorisedProps {
  jobPost: FragmentType<typeof JobTitleColorisedFragmentDoc> | null | undefined
  commonWordColor?: TextColor
  focusWordColor?: TextColor
  textVariant?: TextVariant
  maxLines?: number | undefined
  className?: string
}

export const JobTitleColorised: React.FC<JobTitleColorisedProps> = ({
  commonWordColor = "text-fg-3",
  focusWordColor = "text-fg-1",
  textVariant = "body1",
  maxLines,
  className,
  ...props
}) => {
  let jobTitleParts = getJobTitleParts({ jobPost: props.jobPost, commonWordColor, focusWordColor })

  return (
    <Text variant={textVariant} maxLines={maxLines} className={className}>
      {jobTitleParts.map(part => (
        <Text variant={textVariant} className={part.className} element="span" key={part.key}>
          {part.text}
        </Text>
      ))}
    </Text>
  )
}
